import * as React from 'react';
import { useEffect, useState } from 'react';
import type { QueryResult } from 'react-apollo';

import type {
  XapiIntegration_ExternalLearningPlatform,
  XapiIntegration_XapiActorIdentificationType,
} from '__generated__/graphql-types';
import type Maybe from 'graphql/tsutils/Maybe';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import type {
  GetXapiIntegrationQueryVariables,
  GetXapiIntegrationQuery as GetXapiIntegrationResponse,
} from 'bundles/enterprise-admin-xapi/components/__generated__/GetXapiIntegration';

import GetXapiIntegrationQueryWrapper from './GetXapiIntegrationQueryWrapper';

export type IntegrationClientCredentials = {
  // eslint-disable-next-line camelcase
  externalLearningPlatform?: Maybe<XapiIntegration_ExternalLearningPlatform>;
  // eslint-disable-next-line camelcase
  agentActorIdentificationType?: Maybe<XapiIntegration_XapiActorIdentificationType>;
  clientId: string;
  clientSecret: string;
  openAuthorizationUrl: string;
  organizationId: string;
  tenantUrl: string;
};

export const INTEGRATION_CLIENT_CREDENTIALS_EMPTY_VALUES = {
  clientId: '',
  clientSecret: '',
  openAuthorizationUrl: '',
  organizationId: '',
  tenantUrl: '',
};

type XapiContextType = {
  xapiVariable?: IntegrationClientCredentials;
  setXapiVariable: (x?: IntegrationClientCredentials) => void;
  query?: QueryResult<GetXapiIntegrationResponse, GetXapiIntegrationQueryVariables>;
};

export const XapiContext = React.createContext<XapiContextType>({
  xapiVariable: INTEGRATION_CLIENT_CREDENTIALS_EMPTY_VALUES,
  setXapiVariable: () => null,
  query: undefined,
});

type Props = {
  children: React.ReactElement;
  xApiInegrationQueryResult: QueryResult<GetXapiIntegrationResponse, GetXapiIntegrationQueryVariables>;
};

export const XapiContextProvider: React.FC<Props> = ({ children, xApiInegrationQueryResult, ...props }: Props) => {
  const [xapiVariable, setXapiVariable] = React.useState<IntegrationClientCredentials | undefined>(undefined);
  const [query, setQuery] = useState<QueryResult<GetXapiIntegrationResponse, GetXapiIntegrationQueryVariables>>();

  useEffect(() => {
    setQuery(xApiInegrationQueryResult);
  }, [xApiInegrationQueryResult]);

  useEffect(() => {
    const xapiIntegrationConfiguration =
      xApiInegrationQueryResult?.data?.XapiIntegration?.getXapiIntegration?.xapiIntegrationConfiguration;
    const xapiIntegrationClientCredentials =
      xApiInegrationQueryResult?.data?.XapiIntegration?.getXapiIntegration?.xapiIntegrationClientCredentials;

    setXapiVariable({
      externalLearningPlatform: xapiIntegrationClientCredentials?.externalLearningPlatform,
      agentActorIdentificationType: xapiIntegrationConfiguration?.agentActorIdentificationType,
      clientId: xapiIntegrationClientCredentials?.clientId ?? '',
      clientSecret: xapiIntegrationClientCredentials?.clientSecret ?? '',
      openAuthorizationUrl: xapiIntegrationClientCredentials?.openAuthorizationUrl ?? '',
      organizationId: xapiIntegrationClientCredentials?.organizationId ?? '',
      tenantUrl: xapiIntegrationClientCredentials?.tenantUrl ?? '',
    });
  }, [xApiInegrationQueryResult?.data?.XapiIntegration?.getXapiIntegration]);

  return (
    <>
      <XapiContext.Provider
        value={{
          xapiVariable,
          setXapiVariable,
          query,
        }}
      >
        {children && React.cloneElement(children, props)}
      </XapiContext.Provider>
    </>
  );
};

export function useXapiContext() {
  const context = React.useContext(XapiContext);
  if (context == null) {
    throw new Error('useXapiContextProvider should be used within XapiContextProvider');
  }
  return context;
}

export const XapiContextProviderWrapper: React.FC<{
  children: React.ReactElement;
}> = ({ children, ...props }) => {
  const org = useThirdPartyOrganization();
  return (
    <GetXapiIntegrationQueryWrapper variables={{ input: { organizationId: org.id } }}>
      {(xApiInegrationQueryResult: QueryResult<GetXapiIntegrationResponse, GetXapiIntegrationQueryVariables>) => (
        <XapiContextProvider {...props} xApiInegrationQueryResult={xApiInegrationQueryResult}>
          {children && React.cloneElement(children, props)}
        </XapiContextProvider>
      )}
    </GetXapiIntegrationQueryWrapper>
  );
};

export default XapiContextProviderWrapper;
